import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import QuizTaking from './QuizTaking';
import QuizResults from './QuizResults';
import api from '../api';

const StudentQuizzes = () => {
    const [quizzes, setQuizzes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeQuiz, setActiveQuiz] = useState(null);
    const [quizAttempt, setQuizAttempt] = useState(null);
    const [showResults, setShowResults] = useState(null);

    useEffect(() => {
        fetchQuizzes();
    }, []);

    const fetchQuizzes = async () => {
        try {
            setLoading(true);
            const res = await api.get('/quizzes');
            // Handle both old format (array) and new paginated format
            const quizzesData = res.data.success && res.data.quizzes
                ? res.data.quizzes.data
                : res.data;
            setQuizzes(Array.isArray(quizzesData) ? quizzesData : []);
        } catch (error) {
            console.error('Error fetching quizzes:', error);
        } finally {
            setLoading(false);
        }
    };

    const startQuiz = async (quiz) => {
        try {
            setLoading(true);
            const res = await api.post(`/quizzes/${quiz._id}/attempt`);
            setQuizAttempt(res.data);
            setActiveQuiz(quiz);
        } catch (error) {
            console.error('Error starting quiz:', error);
            alert('Error starting quiz. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const finishQuiz = () => {
        setActiveQuiz(null);
        setQuizAttempt(null);
        fetchQuizzes(); // Refresh to update attempt counts
    };

    const showQuizResults = (quiz) => {
        setShowResults(quiz);
    };

    const hideQuizResults = () => {
        setShowResults(null);
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'No limit';
        return new Date(dateString).toLocaleDateString('vi-VN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Component to render text with math support
    const MathRenderer = ({ children }) => {
        if (!children) return null;
        return (
            <div className="prose prose-slate max-w-none text-sm">
                <ReactMarkdown
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                >
                    {children}
                </ReactMarkdown>
            </div>
        );
    };

    if (activeQuiz && quizAttempt) {
        return (
            <QuizTaking
                quiz={activeQuiz}
                attempt={quizAttempt}
                onFinish={finishQuiz}
            />
        );
    }

    if (showResults) {
        return (
            <QuizResults
                quiz={showResults}
                attempt={showResults.lastAttempt}
                onBack={hideQuizResults}
            />
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-lg text-slate-600">Loading quizzes...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="text-center">
                <h2 className="text-3xl font-bold text-slate-900 mb-2">📝 Bài tập</h2>
                <p className="text-slate-600">Hoàn thành bài tập để kiểm tra kiến thức của bạn</p>
            </div>

            {quizzes.length === 0 ? (
                <div className="text-center py-12">
                    <div className="text-6xl mb-4">📚</div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Chưa có bài tập nào</h3>
                    <p className="text-slate-600">Hiện tại chưa có bài tập nào được giao cho lớp của bạn.</p>
                </div>
            ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {quizzes.map((quiz) => (
                        <div key={quiz._id} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-200">
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{quiz.title}</h3>
                                    {quiz.description && (
                                        <div className="text-slate-600 mb-3">
                                            <MathRenderer>{quiz.description}</MathRenderer>
                                        </div>
                                    )}
                                </div>
                                <div className="text-2xl">📝</div>
                            </div>

                            <div className="space-y-3 mb-4">
                                <div className="flex items-center text-sm text-slate-600">
                                    <span className="w-4 h-4 mr-2">❓</span>
                                    <span>{quiz.questions.length} câu hỏi</span>
                                </div>

                                {quiz.timeLimit && (
                                    <div className="flex items-center text-sm text-slate-600">
                                        <span className="w-4 h-4 mr-2">⏱️</span>
                                        <span>{quiz.timeLimit} phút</span>
                                    </div>
                                )}

                                <div className="flex items-center text-sm text-slate-600">
                                    <span className="w-4 h-4 mr-2">🏆</span>
                                    <span>{quiz.userAttempts ? 'Đã có điểm tốt nhất' : 'Chưa làm bài'}</span>
                                </div>

                                {quiz.availableFrom && (
                                    <div className="flex items-center text-sm text-slate-600">
                                        <span className="w-4 h-4 mr-2">📅</span>
                                        <span>Từ: {formatDateTime(quiz.availableFrom)}</span>
                                    </div>
                                )}

                                {quiz.availableUntil && (
                                    <div className="flex items-center text-sm text-slate-600">
                                        <span className="w-4 h-4 mr-2">⏳</span>
                                        <span>Đến: {formatDateTime(quiz.availableUntil)}</span>
                                    </div>
                                )}

                                <div className="flex flex-wrap gap-1 mt-2">
                                    {quiz.visibleToClasses.map((classItem) => (
                                        <span key={classItem._id} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                            {classItem.name}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {quiz.lastAttempt && (
                                <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg p-3 mb-4">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-slate-700 font-medium flex items-center">
                                            <span className="mr-2">🏆</span>
                                            Điểm tốt nhất:
                                        </span>
                                        <div className="text-right">
                                            <div className={`font-bold ${
                                                quiz.lastAttempt.percentage >= 80 ? 'text-green-600' :
                                                quiz.lastAttempt.percentage >= 60 ? 'text-yellow-600' :
                                                'text-red-600'
                                            }`}>
                                                {quiz.lastAttempt.score}/{quiz.lastAttempt.totalPoints} ({quiz.lastAttempt.percentage.toFixed(1)}%)
                                            </div>
                                            <div className="text-xs text-slate-500">
                                                {formatDateTime(quiz.lastAttempt.submittedAt)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-2">
                                <button
                                    onClick={() => startQuiz(quiz)}
                                    disabled={loading || !quiz.canAttempt}
                                    className={`flex-1 px-4 py-2 rounded-xl font-medium text-sm transition-colors ${
                                        quiz.canAttempt
                                            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                            : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                    }`}
                                >
                                    {loading ? 'Đang tải...' : quiz.canAttempt ? (quiz.lastAttempt ? 'Thử cải thiện điểm' : 'Bắt đầu làm bài') : 'Hết lượt thử'}
                                </button>

                                {quiz.lastAttempt && quiz.showResults && (
                                    <button
                                        onClick={() => showQuizResults(quiz)}
                                        className="px-4 py-2 rounded-xl font-medium text-sm bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                                    >
                                        {quiz.showCorrectAnswers ? 'Xem đáp án' : 'Xem kết quả'}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default StudentQuizzes;